import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { trackPurchaseStarted } from "../analytics/events";

import logger from "utils/logger";
// Initialize Stripe with publishable key
let stripePromise = null;

export const getStripe = (publishableKey) => {
  if (!stripePromise && publishableKey) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

// Get Stripe publishable key from env
export const getPublishableKey = () => {
  return (
    import.meta.env.VITE_REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    "pk_live_51SJzttB3dLveUWAi9OsI8rnWwcl6PCxlPStDBhQy8pHIYGNjxIRC4rJOex2ghII5VswLAttpRpW0RXHHsemzDYAL00gVxdva10"
  );
};

/**
 * Create a Stripe Checkout session
 * @param {string} priceId - The Stripe Price ID for the selected plan
 * @returns {Promise<Object>} - Returns sessionId and publishableKey
 */
export async function createCheckoutSessionAPI(priceId) {
  try {
    // Track purchase started
    trackPurchaseStarted(priceId);

    const response = await axios.post("/v1/stripe/create-checkout-session", {
      priceId,
      successUrl: `${window.location.origin}/dash/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${window.location.origin}/subscription/cancel`,
    });

    return response;
  } catch (error) {
    logger.error("Erro ao criar sessão de checkout:", error);
    throw error;
  }
}

/**
 * Create a Stripe Customer Portal session
 * @returns {Promise<Object>} - Returns portal URL
 */
export function createPortalSessionAPI() {
  return axios.post("/v1/stripe/create-portal-session", {
    returnUrl: `${window.location.origin}/subscription-plans`,
  });
}

/**
 * Get subscription status
 * @returns {Promise<Object>} - Returns subscription data
 */
export function getSubscriptionStatus() {
  return axios.get("/v1/stripe/subscription-status");
}

/**
 * Verify checkout session
 * @param {string} sessionId - The Stripe session ID
 * @returns {Promise<Object>} - Returns verification result
 */
export function verifySession(sessionId) {
  return axios.get(`/v1/stripe/verify-session?session_id=${sessionId}`);
}

/**
 * Create a Payment Intent for subscription checkout
 * IMPORTANTE: Agora retorna uma Checkout Session URL para assinatura recorrente
 * @param {string} priceId - The Stripe Price ID for the selected plan
 * @returns {Promise<Object>} - Returns sessionId, sessionUrl and publishableKey
 */
export function createPaymentIntent(priceId) {
  return axios.post("/v1/stripe/create-payment-intent", {
    priceId,
    successUrl: `${window.location.origin}/dash/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
    cancelUrl: `${window.location.origin}/subscription-plans`,
  });
}

/**
 * Cria uma sessão de checkout de DOAÇÃO de assinatura para outro usuário.
 * A cobrança vai no cartão de quem doa; o plano é concedido ao presenteado.
 * Apenas planos avulsos (7 ou 30 dias) são aceitos pelo backend.
 * @param {string} recipientUserId - user_id do presenteado
 * @param {string} priceId - Stripe Price ID do plano avulso escolhido
 * @returns {Promise<Object>} - Returns sessionId, sessionUrl and recipientName
 */
export function createGiftCheckoutSession(recipientUserId, priceId) {
  return axios.post("/v1/stripe/create-gift-checkout-session", {
    recipientUserId,
    priceId,
    successUrl: `${window.location.origin}/dash/subscription/success?gift=1`,
    cancelUrl: `${window.location.origin}/dash/profile-details?userUid=${recipientUserId}`,
  });
}

/**
 * Create an Embedded Checkout Session for subscriptions
 * @param {string} priceId - The Stripe Price ID for the selected plan
 * @returns {Promise<Object>} - Returns clientSecret for embedded checkout
 */
export function createEmbeddedCheckoutSession(priceId) {
  return axios.post("/v1/stripe/create-embedded-checkout-session", {
    priceId,
  });
}

/**
 * Confirm payment after user completes the form
 * @param {string} paymentIntentId - The Payment Intent ID
 * @returns {Promise<Object>} - Returns confirmation result
 */
export function confirmPayment(paymentIntentId) {
  return axios.post("/v1/stripe/confirm-payment", {
    paymentIntentId,
  });
}

/**
 * Create a Stripe Customer Portal session and redirect to portal
 * @returns {Promise<void>}
 */
export const createPortalSession = async () => {
  try {
    const response = await createPortalSessionAPI();
    const { url } = response.data;

    // Redirect to Stripe Customer Portal
    window.location.href = url;
  } catch (error) {
    logger.error("Error creating portal session:", error);
    throw error;
  }
};
